/**
 * The sakura scene: the painted layers in `public/focus/sakura`, brought to
 * life with PixiJS (WebGL) - drifting clouds, wind through the meadow and the
 * canopy, glinting water, a tree that sways from its trunk, blossom that comes
 * in stage by stage, and petals that tumble down in the last round.
 *
 * One canvas for the whole app: the page's scene box and focus mode both hand
 * it a slot (`attach`), and only the slot on screen holds it. PixiJS is
 * imported on first attach, so nobody who never picks the Sakura view pays
 * for it. If WebGL or the import fails, the slot keeps its poster still.
 */
import type * as PixiNS from 'pixi.js';

type Pixi = typeof PixiNS;

export interface SakuraSceneState {
  bloom: number;
  fall: number;
  falling: boolean;
  phase: 'idle' | 'focus' | 'paused' | 'break';
}

const W = 1920;
const H = 1080;
/** The trunk base: the sway pivot. */
const PIVOT = { x: 1254, y: 942 };
/** Night: the moon sits tucked into the top-left corner. */
const MOON_OFFSET = { x: -150, y: -60 };
const BASE = '/focus/sakura';
const LAYERS = [
  '00-sky',
  '01-clouds-far-loop',
  '02-clouds-near-loop',
  '03-mountains',
  '04-hills-lake',
  '05-water-glints',
  '06-meadow-back',
  '07-tree-bare',
  '08-bloom-1',
  '08-bloom-2',
  '08-bloom-3',
  '08-bloom-4',
  '08-bloom-5',
  '09-meadow-front',
  '11-ground-petals',
];
const NIGHT_ONLY = ['00b-stars', '12-moon'];
const PETALS = [1, 2, 3, 4, 5, 6, 7, 8].map(
  i => `${BASE}/petals/petal-${i}.webp`
);

export const sakuraPoster = (night: boolean) =>
  `${BASE}/${night ? 'night' : 'day'}/poster.webp`;

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, n));

interface Petal {
  sp: PixiNS.Sprite;
  base: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  ph: number;
  life: number;
}
interface Wind {
  spr: PixiNS.Sprite;
  f: PixiNS.DisplacementFilter;
  sx: number;
  sy: number;
  speed: number;
}
interface Layers {
  sky: PixiNS.Sprite;
  stars?: PixiNS.Sprite;
  moon?: PixiNS.Sprite;
  cloudsFar: PixiNS.TilingSprite;
  cloudsNear: PixiNS.TilingSprite;
  glints: PixiNS.Sprite;
  tree: PixiNS.Container;
  bloom: PixiNS.Sprite[];
  ground: PixiNS.Sprite;
  petals: PixiNS.Container;
}

let PIXI: Pixi | null = null;
let app: PixiNS.Application | null = null;
let host: HTMLDivElement | null = null;
let world: PixiNS.Container | null = null;
let layers: Layers | null = null;
let currentSet: 'day' | 'night' | null = null;
let failed = false;
/**
 * Refits the canvas whenever the box it sits in changes size. A window resize
 * is not enough: ending a session grows the scene box (the summary replaces
 * the timer's controls) with no window resize, and the canvas kept its old
 * height - the still poster behind it showed through the strip below, a
 * second tree trunk under the first.
 */
let boxWatch: ResizeObserver | null = null;
let state: SakuraSceneState = {
  bloom: 0,
  fall: 0,
  falling: false,
  phase: 'idle',
};
const shown = { bloom: [0, 0, 0, 0, 0], ground: 0, wind: 0 };
let petals: Petal[] = [];
let petalTex: PixiNS.Texture[] = [];
let winds: Wind[] = [];
let spawnClock = 0;
let t = 0;
const reduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A tileable wind map, drawn once: soft overlapping bands, no file to load. */
function windTexture(P: Pixi, seed: number): PixiNS.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const g = c.getContext('2d');
  if (!g) return P.Texture.WHITE;
  const img = g.createImageData(256, 256);
  const k = [1 + (seed % 2), 2 + (seed % 3), 3 + (seed % 4)];
  const ph = [seed * 0.7, seed * 1.3, seed * 2.1];
  for (let y = 0; y < 256; y++) {
    for (let x = 0; x < 256; x++) {
      const u = (x / 256) * Math.PI * 2;
      const v = (y / 256) * Math.PI * 2;
      const n =
        Math.sin(k[0] * u + ph[0]) * 0.5 +
        Math.sin(k[1] * (u + v) + ph[1]) * 0.3 +
        Math.sin(k[2] * v - u + ph[2]) * 0.2;
      const o = (y * 256 + x) * 4;
      img.data[o] = 128 + n * 110;
      img.data[o + 1] = 128 + Math.sin(k[1] * v + ph[2]) * 60;
      img.data[o + 2] = 128;
      img.data[o + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  const tex = P.Texture.from(c);
  tex.baseTexture.wrapMode = P.WRAP_MODES.REPEAT;
  return tex;
}

/** How one layer moves in the wind: the map's seed, its push (x, y), its size. */
type WindSpec = [seed: number, sx: number, sy: number, scale: number];

function addWind(
  P: Pixi,
  root: PixiNS.Container,
  target: PixiNS.Container,
  wind: WindSpec
) {
  const [seed, sx, sy, scale] = wind;
  const spr = new P.Sprite(windTexture(P, seed));
  spr.scale.set(scale);
  spr.renderable = false;
  root.addChild(spr);
  const f = new P.DisplacementFilter(spr, 0);
  f.padding = 24;
  target.filters = [f];
  winds.push({ spr, f, sx, sy, speed: 18 + (seed % 7) });
}

function build(
  P: Pixi,
  set: 'day' | 'night',
  tex: (name: string) => PixiNS.Texture
) {
  if (!app) return;
  if (world) {
    app.stage.removeChildren();
    world.destroy({ children: true });
  }
  winds = [];
  petals = [];
  world = new P.Container();
  app.stage.addChild(world);
  const root = world;
  const sprite = (name: string, parent: PixiNS.Container = root) => {
    const s = new P.Sprite(tex(name));
    s.width = W;
    s.height = H;
    parent.addChild(s);
    return s;
  };
  const sky = sprite('00-sky');
  let stars: PixiNS.Sprite | undefined;
  let moon: PixiNS.Sprite | undefined;
  if (set === 'night') {
    stars = sprite('00b-stars');
    moon = sprite('12-moon');
    moon.position.set(MOON_OFFSET.x, MOON_OFFSET.y);
  }
  // Clouds wrap forever: the loop tiles are seamless horizontally.
  const tiling = (name: string) => {
    const t0 = tex(name);
    const ts = new P.TilingSprite(t0, W, H);
    ts.tileScale.set(W / t0.width, H / t0.height);
    root.addChild(ts);
    return ts;
  };
  const cloudsFar = tiling('01-clouds-far-loop');
  const cloudsNear = tiling('02-clouds-near-loop');
  sprite('03-mountains');
  sprite('04-hills-lake');
  const glints = sprite('05-water-glints');
  const meadowBack = sprite('06-meadow-back');
  // The tree sways from the trunk base; the bloom stages ride on it.
  const tree = new P.Container();
  tree.pivot.set(PIVOT.x, PIVOT.y);
  tree.position.set(PIVOT.x, PIVOT.y);
  world.addChild(tree);
  sprite('07-tree-bare', tree);
  const canopy = new P.Container();
  tree.addChild(canopy);
  const bloom = [1, 2, 3, 4, 5].map(i => {
    const s = sprite(`08-bloom-${i}`, canopy);
    s.alpha = shown.bloom[i - 1];
    return s;
  });
  const meadowFront = sprite('09-meadow-front');
  const ground = sprite('11-ground-petals');
  ground.alpha = shown.ground;
  const petalLayer = new P.Container();
  world.addChild(petalLayer);
  if (!reduced()) {
    addWind(P, root, meadowFront, [3, 7, 3, 3.2]);
    addWind(P, root, meadowBack, [5, 3.5, 1.5, 4]);
    addWind(P, root, glints, [11, 5, 1, 1.4]);
    addWind(P, root, canopy, [7, 2.4, 1.6, 2.2]);
  }
  layers = {
    sky,
    stars,
    moon,
    cloudsFar,
    cloudsNear,
    glints,
    tree,
    bloom,
    ground,
    petals: petalLayer,
  };
  fit();
}

function fit() {
  if (!app || !host?.parentElement) return;
  const w = host.parentElement.clientWidth;
  const h = host.parentElement.clientHeight;
  if (!w || !h) return;
  app.renderer.resize(w, h);
  if (!world) return;
  const s = Math.max(w / W, h / H);
  world.scale.set(s);
  // Portrait (a phone in focus mode): keep the tree in view, not the lake.
  const cx = h > w ? 1150 : W / 2;
  world.x = clamp(w / 2 - cx * s, w - W * s, 0);
  world.y = (h - H * s) / 2;
}

function spawnPetal(P: Pixi, heavy: boolean) {
  if (!layers || !petalTex.length || petals.length > 140) return;
  const sp = new P.Sprite(
    petalTex[Math.floor(Math.random() * petalTex.length)]
  );
  sp.anchor.set(0.5);
  // A few close to the eye: bigger and faster, for depth.
  const near = Math.random() < 0.25;
  sp.x = heavy ? 760 + Math.random() * 1000 : 900 + Math.random() * 800;
  sp.y = 90 + Math.random() * 480;
  sp.alpha = 0;
  if (currentSet === 'night') sp.tint = 0xd9c8ff;
  layers.petals.addChild(sp);
  petals.push({
    sp,
    base: (near ? 0.55 : 0.25) + Math.random() * 0.25,
    vx: -(30 + Math.random() * 60) * (near ? 1.6 : 1),
    vy: (38 + Math.random() * 46) * (near ? 1.5 : 1),
    rot: Math.random() * Math.PI * 2,
    vr: (Math.random() - 0.5) * 2.4,
    ph: Math.random() * Math.PI * 2,
    life: 0,
  });
}

function frame() {
  if (!PIXI || !app || !layers || !host?.parentElement) return;
  const P = PIXI;
  const L = layers;
  const dt = Math.min(0.05, app.ticker.deltaMS / 1000);
  const still = reduced();
  t += dt;
  const ease = (k: number) => 1 - Math.pow(1 - k, dt * 60);
  // Wind: full while focusing, gentle on a break, almost still when paused.
  const windTarget = still
    ? 0
    : state.phase === 'focus'
      ? 1
      : state.phase === 'paused'
        ? 0.12
        : 0.55;
  shown.wind += (windTarget - shown.wind) * ease(0.02);
  const gust = 0.6 + 0.4 * Math.sin(t * 0.23) * Math.sin(t * 0.61);
  if (!still) {
    L.cloudsFar.tilePosition.x -= dt * 4;
    L.cloudsNear.tilePosition.x -= dt * 9;
  }
  L.tree.rotation =
    shown.wind *
    (Math.sin(t * 0.55) * 0.0028 + Math.sin(t * 1.4) * 0.0009) *
    gust;
  winds.forEach(w => {
    w.spr.x -= dt * w.speed;
    w.spr.y += dt * w.speed * 0.3;
    w.f.scale.x = w.sx * shown.wind * gust;
    w.f.scale.y = w.sy * shown.wind * gust;
  });
  L.glints.alpha = still
    ? 0.8
    : 0.55 +
      0.45 *
        (0.5 + 0.5 * Math.sin(t * 1.3)) *
        (0.6 + 0.4 * Math.sin(t * 2.9 + 1));
  if (L.stars) L.stars.alpha = still ? 1 : 0.75 + 0.25 * Math.sin(t * 1.7);
  if (L.moon) L.moon.alpha = 0.92 + 0.08 * Math.sin(t * 0.4);
  // Bloom: each stage eases towards where the session is, so even a jump
  // (a reload, a round skipped) lands softly.
  L.bloom.forEach((s, i) => {
    const target = clamp(state.bloom * 5 - i, 0, 1);
    shown.bloom[i] += (target - shown.bloom[i]) * (still ? 1 : ease(0.012));
    s.alpha = shown.bloom[i];
  });
  shown.ground += (state.fall - shown.ground) * (still ? 1 : ease(0.01));
  L.ground.alpha = shown.ground;
  // Petals: a rare one whenever there is blossom; a steady fall in the last round.
  if (!still) {
    const rate = state.falling ? 5.5 : shown.bloom[1] > 0.5 ? 0.15 : 0;
    spawnClock += dt * rate;
    while (spawnClock > 1) {
      spawnPetal(P, state.falling);
      spawnClock -= 1;
    }
  }
  for (let i = petals.length - 1; i >= 0; i--) {
    const q = petals[i];
    q.life += dt;
    q.sp.x +=
      (q.vx * (0.4 + shown.wind * 0.8) + Math.sin(t * 1.3 + q.ph) * 22) * dt;
    q.sp.y += q.vy * dt;
    q.rot += q.vr * dt;
    q.sp.rotation = q.rot;
    // Tumbling: the petal turns edge-on and back.
    q.sp.scale.set(q.base * Math.cos(t * 2 + q.ph), q.base);
    q.sp.alpha =
      Math.min(1, q.life * 1.5) *
      (q.sp.y > 990 ? Math.max(0, 1 - (q.sp.y - 990) / 60) : 1);
    if (q.sp.y > 1060 || q.sp.x < -60) {
      q.sp.destroy();
      petals.splice(i, 1);
    }
  }
}

async function ensure(): Promise<boolean> {
  if (failed) return false;
  if (app) return true;
  try {
    PIXI = await import('pixi.js');
    host = document.createElement('div');
    host.className =
      'absolute inset-0 [&>canvas]:block [&>canvas]:h-full [&>canvas]:w-full';
    app = new PIXI.Application({
      width: 400,
      height: 240,
      backgroundAlpha: 0,
      antialias: true,
      resolution: Math.min(2, window.devicePixelRatio || 1),
      autoDensity: true,
    });
    host.appendChild(app.view as HTMLCanvasElement);
    app.ticker.add(frame);
    window.addEventListener('resize', fit);
    return true;
  } catch {
    failed = true; // no WebGL: the poster stays
    return false;
  }
}

/** The set asked for last: a slower, older load must not paint over it. */
let wantedSet: 'day' | 'night' | null = null;

async function load(set: 'day' | 'night') {
  wantedSet = set;
  if (!PIXI || currentSet === set) return;
  const P = PIXI;
  const names = [...LAYERS, ...(set === 'night' ? NIGHT_ONLY : [])];
  const urls = names.map(n => `${BASE}/${set}/${n}.webp`);
  await (async () => {
    const [tex, pt] = await Promise.all([
      P.Assets.load(urls) as Promise<Record<string, PixiNS.Texture>>,
      petalTex.length
        ? Promise.resolve(null)
        : (P.Assets.load(PETALS) as Promise<Record<string, PixiNS.Texture>>),
    ]);
    if (pt) petalTex = PETALS.map(u => pt[u]);
    if (wantedSet !== set) return;
    build(P, set, n => tex[`${BASE}/${set}/${n}.webp`]);
    currentSet = set;
  })();
}

export const sakuraStage = {
  /** Move the canvas into `slot` and draw the day or night set there. */
  async attach(slot: HTMLElement, night: boolean): Promise<boolean> {
    if (!(await ensure()) || !app || !host) return false;
    if (host.parentElement !== slot) slot.appendChild(host);
    if (typeof ResizeObserver !== 'undefined') {
      boxWatch ??= new ResizeObserver(() => fit());
      boxWatch.disconnect();
      boxWatch.observe(slot);
    }
    app.ticker.start();
    fit();
    try {
      await load(night ? 'night' : 'day');
    } catch {
      return false;
    }
    fit();
    return true;
  },
  /** Release `slot`; the canvas stops drawing until something attaches it again. */
  detach(slot: HTMLElement) {
    if (host && host.parentElement === slot) {
      boxWatch?.disconnect();
      host.remove();
      app?.ticker.stop();
    }
  },
  setState(next: SakuraSceneState) {
    state = next;
  },
  fit,
};
